import { Injectable, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';

@Injectable({ providedIn: 'root' })
export class ContextMenuService {
  public readonly items = signal<MenuItem[]>([]);

  private menu: ContextMenu | null = null;

  /** ids of the tasks queued by `open` — never allowed to outlive the menu they were queued for */
  private pendingTasks = new Set<ReturnType<typeof setTimeout>>();

  public register(menu: ContextMenu): void {
    this.menu = menu;
  }

  public unregister(): void {
    // the overlay goes away with the host, so only what is kept here has to be dropped
    this.menu = null;
    this.cancelPendingTasks();
    this.items.set([]);
  }

  public reset(): void {
    this.close();
    this.items.set([]);
  }

  public open(event: MouseEvent, items: MenuItem[]): void {
    if (!this.menu || !items.length) return;

    event.preventDefault();
    this.cancelPendingTasks();
    this.items.set(this.applyDisabledGuard(items));

    this.defer(() => {
      this.menu?.show(event);

      // PrimeNG positions the overlay before the new items are rendered, so the flip/fit
      // calculation has to run again once the real size is known.
      this.defer(() => (this.menu?.visible() ? this.menu.position() : undefined));
    });
  }

  public close(): void {
    this.cancelPendingTasks();
    this.menu?.hide();
  }

  private defer(task: () => void): void {
    const id = setTimeout(() => {
      this.pendingTasks.delete(id);

      if (this.menu) task();
    });

    this.pendingTasks.add(id);
  }

  private cancelPendingTasks(): void {
    this.pendingTasks.forEach(clearTimeout);
    this.pendingTasks.clear();
  }

  private applyDisabledGuard(items: MenuItem[]): MenuItem[] {
    return items.map(item => ({
      ...item,
      command: item.disabled ? undefined : item.command,
      items: item.items?.length ? this.applyDisabledGuard(item.items as MenuItem[]) : item.items,
    }));
  }
}
